import { useLayoutEffect, useRef, useState } from 'react';

import {
  useBackendContext,
  useEventContext,
  isFocusVisible,
  isMobileDevice,
  useConfigContext,
  QuestionAnswerModal,
  AnswersComponent,
  useComponentMounted,
} from '@omq/shared';

import { URLStore } from '../../stores/url-store';
import type { HelpQuestion } from '../../api/help';
import { HelpAPI } from '../../api/help';
import { keepAutoComplete } from '../../utils/keep-auto-complete';
import { keyboardAnchorProps } from '../../utils/keyboard-anchor';
import { usePrintQuestion } from '@omq/shared/src/hooks/print-question';
import { ConfigAnswerDisplayTypes } from '@omq/types';

/**
 * Type for component properties.
 */
type QuestionItemProps = {
  /**
   * Question to display.
   */
  question: HelpQuestion;

  /**
   * Flag if question is active.
   * If true, answers are displayed.
   */
  isActive: boolean;

  /**
   * Click handler for select/deselect.
   * If question is selected, question is passed as param,
   * otherwise null.
   */
  onQuestionClick: ((question: HelpQuestion | null) => void) | null | undefined;

  /**
   * If the question is opened in the detailed view singly
   */
  isInDetailView: boolean;
};

/**
 * Question item that is rendered inside QuestionList component.
 *
 * @param {QuestionItemProps} props - Component properties.
 *
 * @author Florian Walch
 * @since 9.2
 *
 * @returns {JSX.Element}
 */
export function QuestionItem(props: QuestionItemProps): JSX.Element {
  // get props
  const { question, isActive, onQuestionClick, isInDetailView } = props;

  const backend = useBackendContext();
  const pageEvent = useEventContext();
  const isMobile = isMobileDevice();
  const config = useConfigContext();

  // store focus state of link element bc
  // it is used to update the class/style of the wrapping element
  const [hasFocus, setHasFocus] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>();

  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState<boolean>(false);

  // remember the previous active state to detect a collapse
  const wasActive = useRef<boolean>(isActive);

  // Restore keyboard focus to the question header link when the question
  // collapses while the focus was inside the removed answer content -
  // otherwise the focus falls back to document.body and keyboard users
  // lose their position. Layout effect: runs after the unmount but before
  // paint, so focus never visibly sits on <body>.
  useLayoutEffect(() => {
    if (wasActive.current && !isActive) {
      const activeElement = document.activeElement;

      if ((activeElement == null || activeElement === document.body) && linkRef.current != null) {
        linkRef.current.focus();
      }
    }

    wasActive.current = isActive;
  }, [isActive]);

  // Opens the browser's print dialog and applies print specific CSS
  // to be able to print the active item
  usePrintQuestion({
    isPrintEnabled: isPrintDialogOpen,
    setIsPrintEnabled: setIsPrintDialogOpen,
    filePath: '/help/assets/print-style.css',
  });

  // scroll to activated item
  useComponentMounted(() => {
    const link = linkRef.current;
    if (isActive && link != null && link.scrollIntoView != null) {
      link.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [isActive]);

  // create click handler
  const handleQuestionClick = (event: React.SyntheticEvent) => {
    event.preventDefault();

    // Do not close the question if the print button has been clicked
    if (
      onQuestionClick != null &&
      !(event.target as HTMLElement)?.classList.contains('omq-question-list__print-button') &&
      !(event.target as HTMLElement)?.classList.contains('omq-question-list__print-icon')
    ) {
      onQuestionClick(isActive ? null : question);
    }

    if (!isActive) {
      pageEvent.dispatchQuestionClick(question);
      HelpAPI.trackView(backend, question.id);
    }
  };

  const isInline = config.answerDisplayType === ConfigAnswerDisplayTypes.INLINE;

  // Determine if the print button should be displayed in the question's header bar.
  // Only for the inline variant here, otherwise the icon will appear in the bar
  // in the background of the modal. The modal already has it in its own header bar.
  const displayPrintButton = config.isPrintingActive && isActive && isInline;

  // Print button component as a variable to reuse
  const printButton = (
    <button
      onClick={() => setIsPrintDialogOpen(true)}
      title={config.loc('print')}
      className={config.generateClassName('question-list__print-button')}>
      <i className={config.generateClassName('question-list__print-icon')} />
    </button>
  );

  // render content of question
  const renderQuestionContent = (): JSX.Element => {
    // Force modal if we are in mobile mode and the mobile search is also a popup,
    // that is done because in mobile it makes much more sense to have a popup,
    // because of the space we have on the small screen for a search input ->
    // Google does a similar thing
    const forceModal = isMobile && config.mobileSearchDisplayType === 'POPUP';

    // popup or force the modal mode
    const isModal = config.answerDisplayType === 'POPUP' || forceModal;

    // get answers
    const answers = (
      <AnswersComponent
        question={question}
        className="help"
        isModal={isModal}
        printButton={displayPrintButton && !isModal ? printButton : null}
      />
    );

    // wrap answers in a modal on mobile devices
    if (isModal) {
      const onClose = () => {
        onQuestionClick != null ? onQuestionClick(null) : undefined;

        if (linkRef.current != null) {
          linkRef.current.focus();
        }
      };

      return (
        <QuestionAnswerModal headline={question.body} className="help" onClose={onClose}>
          {answers}
        </QuestionAnswerModal>
      );
    }

    // render answers without modal.
    return answers;
  };

  // create class names
  let className = config.generateClassName('question-list__question');
  className += isActive ? ` ${config.generateClassName('question-list__question--active')}` : '';

  className += isInDetailView
    ? ` ${config.generateClassName('question-list__question--detail-view')}`
    : '';

  className += hasFocus ? ` ${config.generateClassName('question-list__question--focus')}` : '';

  // Rotate the toggle icon: For popup, it points to the right,
  // for inline, it points to the bottom when closed and to the top when opened
  let toggleIconClassName = config.generateClassName('question-list__toggle-icon');

  if (isInline) {
    toggleIconClassName += isActive
      ? ` ${config.generateClassName('question-list__toggle-icon--up')}`
      : ` ${config.generateClassName('question-list__toggle-icon--down')}`;
  }

  const { body, url, id } = question;

  // render question
  return (
    <div className={className}>
      {isInDetailView ? (
        <span className={config.generateClassName('question-list__link')}>
          <div
            className={config.generateClassName(
              'question-list__print-icon-container--detail-view',
            )}>
            {body}
          </div>
        </span>
      ) : (
        <a
          href={config.isUrlSyncActive ? URLStore.buildAbsolutePath(url) : undefined}
          title={body}
          className={config.generateClassName('question-list__link')}
          role="button"
          onMouseDown={(evt) =>
            keepAutoComplete(evt, `.${config.generateClassName('search__input')}`)
          }
          aria-controls={`aria-answers-${id}`}
          id={`aria-question-${id}`}
          aria-expanded={isActive}
          ref={linkRef}
          onClick={handleQuestionClick}
          onFocus={(event) => setHasFocus(isFocusVisible(event.target as Element))}
          onBlur={() => setHasFocus(false)}
          {...keyboardAnchorProps(config.isUrlSyncActive, handleQuestionClick)}>
          {question.isLoading && (
            <i className={config.generateClassName('question-list__loading-icon')} />
          )}
          <h3>{body}</h3>
          <span className={toggleIconClassName} />
        </a>
      )}

      {isActive && !question.isLoading && renderQuestionContent()}
    </div>
  );
}
